/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Auth0 provider plus device session: claim a slot, heartbeat, and show an eviction modal
 * when another browser takes this account. Axios interceptors are wired here so every API
 * call carries the JWT and X-Session-Id.
 */

import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setupAxiosInterceptors } from "./utils/axiosConfig";
import UserService from "./services/UserService";
import { SessionEvictedModal } from "./components/SessionEvictedModal";
import {
  ensureSession,
  recordSignedOutReason,
  setEvictionHandler,
  startHeartbeat,
  stopHeartbeat,
} from "./utils/sessionManager";
import { auth0Config } from './config/auth0Config';

// Component to setup axios interceptors after Auth0 is initialized
// This must be inside Auth0Provider to access useAuth0 hook
const AxiosInterceptorSetup = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const redirectingRef = useRef(false);
  const [evictionReason, setEvictionReason] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      const getToken = async (options = {}) => {
        try {
          return await getAccessTokenSilently({
            authorizationParams: {
              audience: options.audience || auth0Config.audience,
            },
            cacheMode: options.cacheMode || 'on',
          });
        } catch (error) {
          // Only redirect if user is not authenticated AND we haven't already initiated a redirect
          // Don't redirect if user is authenticated - let the request fail gracefully
          if (!isAuthenticated && !redirectingRef.current) {
            if (error?.error === 'missing_refresh_token' || 
                error?.message?.includes('Missing Refresh Token') ||
                error?.error === 'login_required' ||
                error?.error === 'consent_required') {
              redirectingRef.current = true;
              loginWithRedirect({
                appState: {
                  returnTo: window.location.pathname + window.location.search,
                },
              });
            }
          }
          // Always reject the promise to prevent the request from proceeding
          return Promise.reject(error);
        }
      };

      const claimSession = async () => {
        const token = await getToken();
        const response = await UserService.claimSession(token);
        return response?.data?.sessionId;
      };

      // Setup axios interceptors with token and device session functions
      setupAxiosInterceptors(getToken, claimSession);

      ensureSession(claimSession).catch((error) => {
        console.warn('Could not claim a device session:', error);
      });

      startHeartbeat(async () => {
        const token = await getToken();
        return UserService.sessionHeartbeat(token);
      });

      return () => stopHeartbeat();
    }

    stopHeartbeat();
    return undefined;
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  // Show the reason first; the modal calls back to finish the sign-out.
  useEffect(() => {
    setEvictionHandler((reason) => setEvictionReason(reason));
  }, []);

  // A superseded session must end the Auth0 session too. Clearing only local state would let the
  // SDK silently re-authenticate off the still-valid SSO cookie, and the two devices would evict
  // each other forever.
  const completeSignOut = useCallback(() => {
    recordSignedOutReason(evictionReason);
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [evictionReason, logout]);

  // Reset redirect flag when authentication state changes
  useEffect(() => {
    redirectingRef.current = false;
  }, [isAuthenticated]);

  return (
    <>
      {children}
      {evictionReason && (
        <SessionEvictedModal reason={evictionReason} onSignOut={completeSignOut} />
      )}
    </>
  );
};

export const Auth0ProviderWithNavigate = ({ children }) => {
  const navigate = useNavigate();

  const domain = auth0Config.domain;
  const clientId = auth0Config.clientId;
  const redirectUri = auth0Config.callbackUrl;
  const audience = auth0Config.audience;

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  const onError = (error) => {
    console.error('Auth0 Error:', error);
    // Handle specific error types
    if (error?.error === 'login_required') {
      // User needs to login - handled by AuthenticationGuard
      return;
    }
    // Log other errors for debugging
    if (error?.error !== 'login_required') {
      console.error('Unexpected Auth0 error:', error);
    }
  };

  if (!(domain && clientId && redirectUri && audience)) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Configuration Error</h1>
        <p>Auth0 configuration is missing. Set REACT_APP_AUTH0_* or auth0Config.json.</p>
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        audience: audience,
        redirect_uri: redirectUri,
      }}
      onRedirectCallback={onRedirectCallback}
      onError={onError}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      advancedOptions={{
        defaultScope: 'openid profile email offline_access'
      }}
    >
      <AxiosInterceptorSetup>
        {children}
      </AxiosInterceptorSetup>
    </Auth0Provider>
  );
};
